<!-- app-subdomain-creators/src/Components/CreatorChatMp3Player.vue -->
<template>
  <div class="flex flex-col items-start gap-2">
    <div
      class="relative border border-zinc-400 rounded-xl w-full sm:w-[260px] xl:w-[360px] bg-zinc-800 px-3 py-2"
    >
      <!-- hidden native audio element -->
      <audio
        ref="audioRef"
        :src="src"
        preload="metadata"
        @timeupdate="handleTimeUpdate"
        @loadedmetadata="handleLoadedMetadata"
        @ended="handleEnded"
      ></audio>

      <!-- STACKED LAYOUT -->
      <div class="flex flex-col items-center w-full gap-1">
        <!-- 1) Play / pause centered -->
        <div class="flex justify-center w-full">
          <div
            @click="togglePlay"
            class="w-10 h-10 cursor-pointer rounded-full flex items-center justify-center p-2 bg-[#22c55e] hover:bg-[#4ade80] transition duration-200 ease-in-out transform"
          >
            <PlayIcon
              v-if="!isPlaying"
              class="text-zinc-200 ml-[2px]"
            />
            <PauseIcon
              v-else
              class="text-zinc-200"
            />
          </div>
        </div>

        <!-- 2) Progress row: time - slider - time -->
        <div class="flex items-center w-full gap-2">
          <span class="font-semibold text-sm text-zinc-200 min-w-[3rem] text-left">
            {{ formatTime(currentTime) }}
          </span>

          <input
            type="range"
            class="creator-chat-progress flex-1"
            min="0"
            :max="duration || 0"
            step="0.01"
            :value="isSeeking ? seekValue : currentTime"
            @mousedown.stop="startSeek"
            @touchstart.stop="startSeek"
            @input="updateSeek"
            @change="commitSeek"
            :style="progressStyle"
            />

          <span class="font-semibold text-sm text-zinc-200 min-w-[3rem] text-right">
            {{ formatTime(duration) }}
          </span>
        </div>

        <!-- 3) Volume row (bottom) -->
        <div class="flex items-center justify-between w-full pt-1">
          <!-- file name on the left -->
          <p
            v-if="name"
            class="text-xs font-medium text-zinc-500 truncate max-w-[10rem]"
          >
            {{ name }}
          </p>
          <div v-else class="text-xs text-zinc-500 max-w-[10rem]">
            <!-- empty spacer -->
          </div>

          <!-- volume controls on the right -->
          <div class="flex items-center gap-2 text-xs text-zinc-200">
            <!-- mute button -->
            <button
              type="button"
              @click="toggleMute"
              class="w-6 h-6 flex items-center justify-center rounded-full hover:bg-zinc-700/80"
            >
              <SpeakerXMarkIcon
                v-if="isMuted"
                class="w-4 h-4"
              />
              <SpeakerWaveIcon
                v-else
                class="w-4 h-4"
              />
            </button>

            <!-- volume slider -->
            <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            :value="volume"
            @input="onVolumeChange"
            class="creator-chat-volume w-24"
            :style="volumeStyle"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import {
  PlayIcon,
  PauseIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
} from '@heroicons/vue/24/solid';

defineProps({
  src: {
    type: String,
    required: true,
  },
  name: {
    type: String,
    default: '',
  },
});

const audioRef = ref(null);
const isPlaying = ref(false);

const currentTime = ref(0);
const duration = ref(0);

// seek state
const isSeeking = ref(false);
const seekValue = ref(0);
const wasPlayingBeforeSeek = ref(false);

// volume state
const volume = ref(1);
const isMuted = ref(false);
const lastVolumeBeforeMute = ref(1);

const formatTime = (seconds) => {
  const total = Math.floor(seconds || 0);
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  const secsStr = secs < 10 ? `0${secs}` : `${secs}`;
  return `${mins}:${secsStr}`;
};

const syncVolumeToAudio = () => {
  if (!audioRef.value) return;
  audioRef.value.volume = volume.value;
  audioRef.value.muted = isMuted.value;
};

const togglePlay = () => {
  if (!audioRef.value) return;

  if (isPlaying.value) {
    audioRef.value.pause();
    isPlaying.value = false;
  } else {
    syncVolumeToAudio();
    audioRef.value
      .play()
      .then(() => {
        isPlaying.value = true;
      })
      .catch((err) => {
        console.warn('[CreatorChatMp3Player] play failed', err);
      });
  }
};

const handleLoadedMetadata = () => {
  if (!audioRef.value) return;
  duration.value = Number.isFinite(audioRef.value.duration)
    ? audioRef.value.duration
    : 0;

  const cur = Number.isFinite(audioRef.value.currentTime)
    ? audioRef.value.currentTime
    : 0;
  currentTime.value = cur;
  seekValue.value = cur;
};

const handleTimeUpdate = () => {
  if (!audioRef.value) return;

  if (isSeeking.value) return;

  const cur = Number.isFinite(audioRef.value.currentTime)
    ? audioRef.value.currentTime
    : 0;
  const dur = Number.isFinite(audioRef.value.duration)
    ? audioRef.value.duration
    : duration.value;

  currentTime.value = cur;
  duration.value = dur;
  seekValue.value = cur;
};

const handleEnded = () => {
  isPlaying.value = false;
  currentTime.value = 0;
  seekValue.value = 0;
};

/**
 * SEEK HANDLERS
 */
const startSeek = () => {
  if (!audioRef.value) return;

  isSeeking.value = true;
  wasPlayingBeforeSeek.value = isPlaying.value;

  if (isPlaying.value) {
    audioRef.value.pause();
    isPlaying.value = false;
  }

  seekValue.value = currentTime.value;
};

const updateSeek = (event) => {
  if (!audioRef.value) return;

  const max =
    duration.value ||
    (Number.isFinite(audioRef.value.duration) ? audioRef.value.duration : 0);

  if (!max) return;

  const raw = Number(event.target.value);
  if (Number.isNaN(raw)) return;

  const clamped = Math.min(max, Math.max(0, raw));

  seekValue.value = clamped;
  currentTime.value = clamped;
};

const commitSeek = (event) => {
  if (!audioRef.value) {
    isSeeking.value = false;
    wasPlayingBeforeSeek.value = false;
    return;
  }

  const max =
    duration.value ||
    (Number.isFinite(audioRef.value.duration) ? audioRef.value.duration : 0);

  if (!max) {
    isSeeking.value = false;
    wasPlayingBeforeSeek.value = false;
    return;
  }

  const raw = event?.target
    ? Number(event.target.value)
    : seekValue.value;

  if (Number.isNaN(raw)) {
    isSeeking.value = false;
    wasPlayingBeforeSeek.value = false;
    return;
  }

  const clamped = Math.min(max, Math.max(0, raw));

  try {
    audioRef.value.currentTime = clamped;
  } catch (e) {
    console.warn('[CreatorChatMp3Player] seek failed', e);
  }

  currentTime.value = clamped;
  seekValue.value = clamped;

  const resume = wasPlayingBeforeSeek.value;
  isSeeking.value = false;
  wasPlayingBeforeSeek.value = false;

  if (resume) {
    syncVolumeToAudio();
    audioRef.value
      .play()
      .then(() => {
        isPlaying.value = true;
      })
      .catch((err) => {
        console.warn('[CreatorChatMp3Player] resume after seek failed', err);
      });
  }
};

/**
 * VOLUME
 */
const onVolumeChange = (event) => {
  const val = Number(event.target.value);
  if (Number.isNaN(val)) return;

  volume.value = Math.min(1, Math.max(0, val));

  if (volume.value <= 0) {
    volume.value = 0;
    isMuted.value = true;
  } else {
    isMuted.value = false;
    lastVolumeBeforeMute.value = volume.value;
  }

  syncVolumeToAudio();
};

const toggleMute = () => {
  if (!audioRef.value) return;

  if (!isMuted.value) {
    lastVolumeBeforeMute.value = volume.value > 0 ? volume.value : 0.5;
    volume.value = 0;
    isMuted.value = true;
  } else {
    volume.value =
      lastVolumeBeforeMute.value > 0 ? lastVolumeBeforeMute.value : 0.5;
    isMuted.value = false;
  }

  syncVolumeToAudio();
};

const progressPercent = computed(() => {
  if (!duration.value || !Number.isFinite(duration.value)) return 0;
  return Math.max(
    0,
    Math.min(100, (currentTime.value / duration.value) * 100),
  );
});

const progressStyle = computed(() => ({
  '--range-progress': `${progressPercent.value}%`,
}));

const volumePercent = computed(() =>
  Math.max(0, Math.min(100, volume.value * 100)),
);

const volumeStyle = computed(() => ({
  '--range-progress': `${volumePercent.value}%`,
}));

</script>

<style scoped>
/* base reset */
.creator-chat-progress,
.creator-chat-volume {
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  border-radius: 9999px;
  height: 0.375rem; /* track height */
  --range-progress: 0%;
}

/* WEBKIT: we draw the fill on the input itself using a gradient */
.creator-chat-progress,
.creator-chat-volume {
  background: linear-gradient(
    to right,
    #22c55e 0%,
    #22c55e var(--range-progress),
    #3f3f46 var(--range-progress),
    #3f3f46 100%
  );
}

.creator-chat-progress::-webkit-slider-runnable-track,
.creator-chat-volume::-webkit-slider-runnable-track {
  height: 0.375rem;
  background: transparent; /* track is the input background */
  border-radius: 9999px;
  border: none;
}

.creator-chat-progress::-webkit-slider-thumb,
.creator-chat-volume::-webkit-slider-thumb {
  -webkit-appearance: none;
  height: 0.75rem;
  width: 0.75rem;
  border-radius: 9999px;
  background-color: #22c55e;
  border: none;
  margin-top: -0.1875rem; /* center thumb on track */
}

/* FIREFOX: supports separate progress + track */
.creator-chat-progress::-moz-range-track,
.creator-chat-volume::-moz-range-track {
  height: 0.375rem;
  background-color: #3f3f46;
  border-radius: 9999px;
  border: none;
}

.creator-chat-progress::-moz-range-progress,
.creator-chat-volume::-moz-range-progress {
  height: 0.375rem;
  background-color: #22c55e;
  border-radius: 9999px;
  border: none;
}

.creator-chat-progress::-moz-range-thumb,
.creator-chat-volume::-moz-range-thumb {
  height: 0.75rem;
  width: 0.75rem;
  border-radius: 9999px;
  background-color: #22c55e;
  border: none;
}

.creator-chat-progress::-moz-focus-outer,
.creator-chat-volume::-moz-focus-outer {
  border: 0;
}

/* optional focus */
.creator-chat-progress:focus-visible,
.creator-chat-volume:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.5);
}
</style>

